"use client";

import { useEffect, useId, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { ANALYTICS_EVENTS, track } from "@/lib/analytics";
import { resolveBg } from "@/lib/bg-color";
import { getBookingSource } from "@/lib/booking-source";
import { isValidEmail } from "@/lib/email";
import { gtmEvent } from "@/lib/gtm";
import { regionFromPathname, regionPath } from "@/lib/region";
import { cn } from "@/lib/utils";
import { Select } from "@/components/ui/select";
import { UnsavedChangesPrompt } from "@/components/ui/unsaved-changes-prompt";

// Mirrors the dialog booking form (booking-drawer.tsx).
const TREATMENTS = [
  "IVF",
  "ICSI",
  "Egg Freezing",
  "IUI",
  "Reciprocal IVF",
  "Fertility Testing",
  "AMH Testing",
  "Donor Sperm",
  "Other",
];
const GENDERS = ["Female", "Male"];
const COUNTRIES = [
  { label: "Ireland", code: "+353" },
  { label: "United Kingdom", code: "+44" },
];

const inputClass =
  "h-12 w-full rounded-xl border border-black/10 bg-white px-4 text-body text-black placeholder:text-black/30 outline-none transition-colors focus:border-purple-03 focus:ring-1 focus:ring-purple-03";
const labelClass = "mb-1.5 block text-caption font-semibold text-black/60";
const optionalClass = "font-normal text-black/35";

type BookingFormBlok = SbBlokData & {
  title?: string;
  intro?: string;
  submit_label?: string;
  consent_label?: string;
  success_heading?: string;
  success_message?: string;
  image?: { filename?: string; alt?: string };
  image_side?: "left" | "right";
  bg_color?: string;
};

type FormState = {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  phone: string;
  gender: string;
  hasPartner: string;
  interestedIn: string;
  message: string;
  consent: boolean;
};

function makeInitial(country: string): FormState {
  return {
    firstName: "",
    lastName: "",
    email: "",
    country,
    phone: "",
    gender: "",
    hasPartner: "",
    interestedIn: "",
    message: "",
    consent: false,
  };
}

export default function BookingForm({ blok }: { blok: BookingFormBlok }) {
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const privacyHref = regionPath("/privacy-policy", region);
  const { bg, onDark } = resolveBg(blok.bg_color, "white");
  const defaultCountry = region === "uk" ? "United Kingdom" : "Ireland";

  const [form, setForm] = useState<FormState>(() => makeInitial(defaultCountry));
  const [touched, setTouched] = useState({ email: false, phone: false });
  const [status, setStatus] = useState<"idle" | "submitting" | "error">("idle");
  const [toast, setToast] = useState(false);
  const [dirty, setDirty] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // useState initial only runs on mount; keep country in sync with region.
  useEffect(() => {
    setForm((prev) => ({ ...prev, country: defaultCountry }));
  }, [defaultCountry]);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(false), 6000);
    return () => clearTimeout(id);
  }, [toast]);

  const update = (field: keyof FormState, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (!dirty) setDirty(true);
  };

  const fieldId = useId();
  const countryId = `${fieldId}-country`;
  const genderId = `${fieldId}-gender`;
  const partnerId = `${fieldId}-partner`;
  const interestId = `${fieldId}-interest`;
  const selectedCountry = COUNTRIES.find((c) => c.label === form.country);
  const phoneLength = form.country === "United Kingdom" ? 10 : 9;
  const emailValid = isValidEmail(form.email);
  const phoneComplete = form.phone.length === phoneLength;
  const emailError = touched.email && form.email.trim() !== "" && !emailValid;
  const phoneError = touched.phone && form.phone !== "" && !phoneComplete;

  const canSubmit =
    form.firstName.trim() !== "" &&
    form.lastName.trim() !== "" &&
    emailValid &&
    phoneComplete &&
    form.gender.trim() !== "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || status === "submitting") return;
    setStatus("submitting");
    try {
      const res = await fetch("/api/booking/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Submission failed");
      const section = getBookingSource(sectionRef.current);
      track(ANALYTICS_EVENTS.BOOKING_SUBMITTED, {
        region,
        source: section === "page" ? "inline-form" : section,
        treatment: form.interestedIn,
        gender: form.gender,
        has_partner: form.hasPartner,
      });
      gtmEvent("booking_submitted", {
        region,
        source: section === "page" ? "inline-form" : section,
        treatment: form.interestedIn,
        gender: form.gender,
        has_partner: form.hasPartner,
      });
      setForm((prev) => makeInitial(prev.country));
      setTouched({ email: false, phone: false });
      setDirty(false);
      setStatus("idle");
      setToast(true);
    } catch {
      setStatus("error");
    }
  };

  const hasImage = Boolean(blok.image?.filename);
  const imageRight = blok.image_side === "right";

  const formColumn = (
    <>
      <div className="flex flex-col gap-4">
        {blok.title && (
          <h2
            className={`text-[clamp(2.25rem,4vw,3rem)] leading-[1.08] font-bold ${
              onDark ? "text-white" : "text-purple-05"
            }`}
          >
            {blok.title}
          </h2>
        )}
        {blok.intro && (
          <p className={`text-body leading-[1.56] ${onDark ? "text-white/80" : "text-black/60"}`}>
            {blok.intro.trim()}
          </p>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className="mt-7 flex flex-col gap-4 rounded-2xl bg-white p-6 lg:p-8"
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label htmlFor={`${fieldId}-firstName`} className={labelClass}>
              First Name
            </label>
            <input
              id={`${fieldId}-firstName`}
              name="firstName"
              type="text"
              required
              placeholder="First name"
              autoComplete="given-name"
              value={form.firstName}
              onChange={(e) => update("firstName", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor={`${fieldId}-lastName`} className={labelClass}>
              Last Name
            </label>
            <input
              id={`${fieldId}-lastName`}
              name="lastName"
              type="text"
              required
              placeholder="Last name"
              autoComplete="family-name"
              value={form.lastName}
              onChange={(e) => update("lastName", e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label htmlFor={`${fieldId}-email`} className={labelClass}>
            Email
          </label>
          <input
            id={`${fieldId}-email`}
            name="email"
            type="email"
            required
            placeholder="you@email.com"
            autoComplete="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            aria-invalid={emailError}
            className={cn(
              inputClass,
              emailError && "border-red-400 focus:border-red-400 focus:ring-red-400",
            )}
          />
          {emailError && (
            <p className="text-caption mt-1 text-red-500">Enter a valid email address</p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_1.5fr]">
          <div>
            <label htmlFor={countryId} className={labelClass}>
              Country
            </label>
            <Select
              id={countryId}
              value={form.country}
              onChange={(v) => update("country", v)}
              options={COUNTRIES.map((c) => ({ value: c.label, label: c.label }))}
            />
          </div>
          <div>
            <label htmlFor={`${fieldId}-phone`} className={labelClass}>
              Phone
            </label>
            <div className="flex">
              <span className="text-button flex h-12 items-center rounded-l-xl border border-r-0 border-black/10 bg-black/3 px-3 text-black/60">
                {selectedCountry?.code}
              </span>
              <input
                id={`${fieldId}-phone`}
                name="phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                required
                placeholder={form.country === "United Kingdom" ? "7123 456789" : "85 123 4567"}
                maxLength={phoneLength}
                value={form.phone}
                onChange={(e) =>
                  update("phone", e.target.value.replace(/\D/g, "").replace(/^0+/, ""))
                }
                onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                aria-invalid={phoneError}
                className={cn(
                  "text-body focus:border-purple-03 focus:ring-purple-03 h-12 w-full rounded-r-xl border border-black/10 bg-white px-4 text-black transition-colors outline-none placeholder:text-black/30 focus:ring-1",
                  phoneError && "border-red-400 focus:border-red-400 focus:ring-red-400",
                )}
              />
            </div>
            {phoneError && (
              <p className="text-caption mt-1 text-red-500">Enter your full phone number</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label htmlFor={genderId} className={labelClass}>
              Gender
            </label>
            <Select
              id={genderId}
              value={form.gender}
              onChange={(v) => update("gender", v)}
              placeholder="Select"
              options={GENDERS.map((g) => ({ value: g, label: g }))}
            />
          </div>
          <div>
            <label htmlFor={partnerId} className={labelClass}>
              Do you have a partner? <span className={optionalClass}>(optional)</span>
            </label>
            <Select
              id={partnerId}
              value={form.hasPartner}
              onChange={(v) => update("hasPartner", v)}
              placeholder="Select"
              options={[
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" },
              ]}
            />
          </div>
        </div>

        <div>
          <label htmlFor={interestId} className={labelClass}>
            I am interested in <span className={optionalClass}>(optional)</span>
          </label>
          <Select
            id={interestId}
            value={form.interestedIn}
            onChange={(v) => update("interestedIn", v)}
            placeholder="Select treatment"
            options={TREATMENTS.map((t) => ({ value: t, label: t }))}
          />
        </div>

        <div>
          <label htmlFor={`${fieldId}-message`} className={labelClass}>
            Message <span className={optionalClass}>(optional)</span>
          </label>
          <textarea
            id={`${fieldId}-message`}
            name="message"
            value={form.message}
            onChange={(e) => update("message", e.target.value)}
            rows={5}
            placeholder="Tell us anything else you'd like us to know"
            className="text-body focus:border-purple-03 focus:ring-purple-03 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-black transition-colors outline-none placeholder:text-black/30 focus:ring-1"
          />
        </div>

        <label className="flex cursor-pointer items-center gap-3">
          <input
            type="checkbox"
            name="consent"
            checked={form.consent}
            onChange={(e) => update("consent", e.target.checked)}
            className="peer sr-only"
          />
          <span
            className={`peer-focus-visible:ring-purple-03 flex size-5 shrink-0 items-center justify-center rounded-md border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2 ${
              form.consent ? "border-purple-03 bg-purple-03" : "border-black/20 bg-white"
            }`}
          >
            {form.consent && (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="size-3.5"
              >
                <path d="m5 12 5 5L20 7" />
              </svg>
            )}
          </span>
          <span className="text-button text-black/70">
            {(blok.consent_label || "Send me updates and fertility tips").trim()}
          </span>
        </label>

        {status === "error" && (
          <p className="text-caption text-red-500">
            Something went wrong. Please try again, or email us directly.
          </p>
        )}

        <button
          type="submit"
          disabled={!canSubmit || status === "submitting"}
          className="bg-purple-03 text-body flex h-14 w-full items-center justify-center rounded-xl font-bold text-white transition-opacity disabled:opacity-40"
        >
          {status === "submitting"
            ? "Submitting…"
            : (blok.submit_label || "Book your free consultation").trim()}
        </button>

        <p className="text-center text-[11px] text-black/60">
          By submitting you agree to our{" "}
          <Link
            href={privacyHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 underline"
          >
            Privacy Policy
            <span className="sr-only">(opens in a new tab)</span>
          </Link>
        </p>
      </form>
    </>
  );

  return (
    <>
      <UnsavedChangesPrompt active={dirty} />
      <section ref={sectionRef} {...storyblokEditable(blok)} className={bg}>
        <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-12 lg:py-16">
          {hasImage ? (
            <div
              className={`flex flex-col gap-10 lg:flex-row lg:items-stretch lg:gap-16 ${
                imageRight ? "lg:flex-row-reverse" : ""
              }`}
            >
              <div className="hidden w-full lg:block lg:w-[45%] lg:shrink-0">
                <div className="relative aspect-4/5 w-full overflow-hidden rounded-[24px] lg:aspect-auto lg:h-full lg:min-h-[520px]">
                  <Image
                    src={blok.image!.filename!}
                    alt={blok.image!.alt || ""}
                    fill
                    sizes="(min-width: 1024px) 45vw, 100vw"
                    className="object-cover"
                  />
                </div>
              </div>
              <div className="w-full lg:flex-1">{formColumn}</div>
            </div>
          ) : (
            <div className="mx-auto w-full max-w-[680px]">{formColumn}</div>
          )}
        </div>
      </section>

      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed top-24 right-4 z-100 w-[min(92vw,400px)] sm:right-8"
        >
          <div className="pointer-events-auto flex animate-[fade-in_180ms_ease-out] items-start gap-4 rounded-[20px] bg-white p-5 text-black shadow-[0_12px_28px_-12px_rgba(20,10,60,0.18)] ring-1 ring-black/5">
            <span className="bg-purple-03 mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full text-white">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="size-4"
                aria-hidden
              >
                <path d="m5 12 5 5L20 7" />
              </svg>
            </span>
            <div className="min-w-0 flex-1 pt-0.5">
              <p className="text-midnight text-[15px] leading-tight font-bold">
                {(blok.success_heading || "Thank you for your enquiry").trim()}
              </p>
              <p className="text-button mt-1 leading-snug text-black/60">
                {(
                  blok.success_message ||
                  "We've received your details and will be in touch within 48 hours to book your consultation."
                ).trim()}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setToast(false)}
              aria-label="Dismiss"
              className="-mt-1 -mr-1 flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-black/40 transition-colors hover:bg-black/5 hover:text-black"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.25"
                strokeLinecap="round"
                className="size-4"
                aria-hidden
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
